import * as React from "react";
import * as Scrivito from "scrivito";
import { LeftSidebarPortal, ObjValidator, XCircleLoader } from "components";
import { useToastCleanup } from "hooks";
import { SideBarWrapper } from "components/LeftSidebar/SideBarWrapper";
import { ProductListLeftSidebar } from "objs/_ConfiguratorObjs/ProductList/ProductListLeftSidebar/ProductListLeftSidebar";
import { ProductsOverview } from "components/ProductsOverview/ProductsOverview";
import {
  useProductsOverviewActions,
  useProductsOverviewStore,
} from "stores/productsOverviewStore";
import { useUser } from "stores/user";
import { useAllProducts } from "hooks/productHooks/useAllProducts";
import { useLanguage } from "stores/globalStore";
import type { ProductNode } from "interfaces/product/product.types";
import type { ObjValidatorProps } from "interfaces/shared.types";
import type { Id } from "react-toastify";

type ProductListPageParams = {
  shouldKeepProductCategories?: boolean;
};

export const ProductList = Scrivito.connect(() => {
  const lang = useLanguage();
  const user = useUser();
  const allProducts = useAllProducts();

  const params = Scrivito.currentPageParams() as ProductListPageParams;
  const { shouldKeepProductCategories } = params;

  const {
    getProductsData,
    setProductsContent,
    setCategoryHeadline,
    setProductCategoryId,
  } = useProductsOverviewActions();

  React.useEffect(() => {
    if (!shouldKeepProductCategories) {
      setProductCategoryId([]);
    }
    if (lang && user) {
      getProductsData({ lang, currentUserTenant: user.tenant });
    }
  }, [
    lang,
    user,
    getProductsData,
    shouldKeepProductCategories,
    setProductCategoryId,
  ]);

  const productCategoryId = useProductsOverviewStore(
    (state) => state.productCategoryId,
  );

  const selectedCategory = React.useMemo(
    () => productCategoryId ?? [],
    [productCategoryId],
  );

  const toastRef = React.useRef<Id | undefined>(undefined);

  useToastCleanup(toastRef);

  React.useEffect(() => {
    if (selectedCategory?.length === 0) {
      setProductsContent(allProducts);
      setCategoryHeadline("");
    } else {
      const findNodes = (prods: ProductNode[] = []) =>
        prods?.forEach((item) => {
          if (item.keyList?.join("/") === selectedCategory?.join("/")) {
            setProductsContent(item.nodes ?? []);
          }
          findNodes(item.nodes ?? []);
        });
      findNodes(allProducts);
    }
  }, [selectedCategory, allProducts, setProductsContent, setCategoryHeadline]);

  return (
    <div className="d-flex flex-grow-1 bg-gray-100 h-full overflow-y-auto overflow-x-hidden">
      {!allProducts.length ? (
        <XCircleLoader />
      ) : (
        <>
          <ProductsOverview isModalActive={false} />
          <LeftSidebarPortal>
            <SideBarWrapper
              width={300}
              isOpenDefault={true}
              withToggle={false}
              toggleText=""
              isRightSidebarToggle={false}
              classNames="sideBarWidth"
            >
              <ProductListLeftSidebar isModalActive={false} />
            </SideBarWrapper>
          </LeftSidebarPortal>
        </>
      )}
    </div>
  );
});

const CheckedPage = (props: ObjValidatorProps) => {
  return (
    <ObjValidator {...props} page={props.page}>
      <ProductList />
    </ObjValidator>
  );
};

export default CheckedPage;
Scrivito.provideComponent("ProductList", CheckedPage);
